// Coaching widget — the threads where the ball is in YOUR court (Ship 3).
//
// "Open threads" is not the useful number: nobody resolves threads, so 447 of
// 462 rows read `status='open'` and a raw open-count is noise. The actionable
// slice is the one Chad's own coaching dashboard computes — a thread you
// started where the LAST message came from someone else, i.e. the rep replied
// and you have not answered. That is ~31 threads, and it is a real to-do list.
// The mirror ("waiting on them") is surfaced as a nudge count only.
//
// Messages are read as (thread_id, author_id, created_at) with NO body, paged
// past the 1,000-row cap — the last-author-per-thread reduction is all the
// widget needs, and skipping bodies keeps the payload small as the table grows.
// Bodies + deal names are fetched only for the handful of rows on screen.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { logCardClick } from "../../../lib/uiEvents";

const SHOW = 4;
// Nudge window is BOUNDED on both ends. Unbounded ("anything I answered last,
// quiet 48h+") reads 176 for Chad — the same noise trap as counting all 447
// "open" threads, because a note the rep never answered sits open forever.
// 2-14 days is the slice that is actually revivable (21 today; 102 more are
// 30d+ and dead).
const NUDGE_MIN_HOURS = 48;
const NUDGE_MAX_DAYS = 14;

interface Row {
  threadId: string;
  dealId: string;
  lastAt: string;
  authorName: string;
  homeowner: string | null;
  preview: string;
}

interface View {
  rows: Row[];
  waitingOnMe: number;
  nudge: number;
}

function timeAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export default function CoachingWidget({ memberId }: { memberId: string }) {
  const [view, setView] = useState<View | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const threads = await supabase
        .from("coaching_threads")
        .select("id,deal_id,status,started_by,created_at")
        .eq("started_by", memberId)
        .eq("status", "open");
      if (cancelled) return;
      if (threads.error) {
        setFailed(true);
        return;
      }
      const mine = (threads.data as Array<{
        id: string;
        deal_id: string;
        created_at: string;
      }>) ?? [];
      if (mine.length === 0) {
        setView({ rows: [], waitingOnMe: 0, nudge: 0 });
        return;
      }
      const mineIds = new Set(mine.map((t) => t.id));

      // Last message per thread. Paged + ordered by id so a tied created_at at
      // a page boundary can't dup/skip (the repo's paging rule).
      const PAGE = 1000;
      const last = new Map<string, { at: string; by: string | null; name: string }>();
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from("coaching_messages")
          .select("thread_id,author_id,author_name,created_at")
          .order("id")
          .range(from, from + PAGE - 1);
        if (cancelled) return;
        if (error || !data) {
          setFailed(true);
          return;
        }
        for (const m of data as Array<{
          thread_id: string;
          author_id: string | null;
          author_name: string | null;
          created_at: string;
        }>) {
          if (!mineIds.has(m.thread_id)) continue;
          const cur = last.get(m.thread_id);
          if (!cur || m.created_at > cur.at) {
            last.set(m.thread_id, {
              at: m.created_at,
              by: m.author_id,
              name: m.author_name ?? "—",
            });
          }
        }
        if (data.length < PAGE) break;
      }

      // Split: someone else spoke last (mine to answer) vs I spoke last.
      const waiting: Array<{ t: (typeof mine)[number]; at: string; name: string }> = [];
      let nudge = 0;
      for (const t of mine) {
        const lm = last.get(t.id);
        if (!lm) {
          // No messages on the thread — nothing has come back to me.
          continue;
        }
        if (lm.by && lm.by === memberId) {
          const ageH = (Date.now() - new Date(lm.at).getTime()) / 3_600_000;
          if (ageH > NUDGE_MIN_HOURS && ageH <= NUDGE_MAX_DAYS * 24) nudge += 1;
        } else {
          waiting.push({ t, at: lm.at, name: lm.name });
        }
      }
      waiting.sort((a, b) => b.at.localeCompare(a.at));

      const top = waiting.slice(0, SHOW);
      const dealIds = [...new Set(top.map((w) => w.t.deal_id).filter(Boolean))];
      const threadIds = top.map((w) => w.t.id);
      const [dealsRes, bodyRes] = await Promise.all([
        dealIds.length
          ? supabase.from("deals").select("id,homeowner_name").in("id", dealIds)
          : Promise.resolve({ data: [], error: null } as any),
        threadIds.length
          ? supabase
              .from("coaching_messages")
              .select("thread_id,body,created_at")
              .in("thread_id", threadIds)
          : Promise.resolve({ data: [], error: null } as any),
      ]);
      if (cancelled) return;

      const nameByDeal = new Map<string, string | null>();
      for (const d of ((dealsRes.data as Array<{ id: string; homeowner_name: string | null }>) ?? []))
        nameByDeal.set(d.id, d.homeowner_name);

      const bodyByThread = new Map<string, { at: string; body: string }>();
      for (const m of ((bodyRes.data as Array<{ thread_id: string; body: string | null; created_at: string }>) ?? [])) {
        const cur = bodyByThread.get(m.thread_id);
        if (!cur || m.created_at > cur.at) {
          bodyByThread.set(m.thread_id, { at: m.created_at, body: m.body ?? "" });
        }
      }

      setView({
        waitingOnMe: waiting.length,
        nudge,
        rows: top.map((w) => ({
          threadId: w.t.id,
          dealId: w.t.deal_id,
          lastAt: w.at,
          authorName: w.name,
          homeowner: nameByDeal.get(w.t.deal_id) ?? null,
          preview: (bodyByThread.get(w.t.id)?.body ?? "").replace(/\s+/g, " ").trim(),
        })),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>💬</span>
        <span className="font-bold text-sm text-bardin-ink">Coaching</span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {failed ? "—" : view ? `${view.waitingOnMe} waiting on you` : "…"}
        </span>
      </div>

      {failed ? (
        <div className="text-xs text-slate-400 py-2">Couldn't load coaching threads — try a refresh.</div>
      ) : !view ? (
        <div className="space-y-2 mt-1">
          <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />
        </div>
      ) : view.rows.length === 0 ? (
        <div className="text-xs text-emerald-700 py-2 font-semibold">
          Nobody's waiting on a reply from you ✓
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {view.rows.map((r) => (
            <Link
              key={r.threadId}
              to={`/deal/${r.dealId}?tab=coaching`}
              onClick={() => logCardClick("widget_coaching_row")}
              className="block py-1.5"
            >
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-bold text-bardin-ink truncate flex-1">
                  {r.homeowner ?? "—"}
                </span>
                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-extrabold whitespace-nowrap">
                  {r.authorName.split(" ")[0]} · {timeAgo(r.lastAt)}
                </span>
              </div>
              {r.preview && (
                <div className="text-[11px] text-slate-500 truncate">{r.preview}</div>
              )}
            </Link>
          ))}
        </div>
      )}

      {view && view.waitingOnMe > view.rows.length && (
        <div className="text-[10px] text-slate-400 font-semibold mt-1">
          +{view.waitingOnMe - view.rows.length} more waiting on you
        </div>
      )}
      {view && view.nudge > 0 && (
        <div className="text-[10px] text-slate-500 font-semibold mt-1.5">
          {view.nudge} thread{view.nudge === 1 ? "" : "s"} you posted went quiet 2–{NUDGE_MAX_DAYS} days ago — worth a
          nudge.
        </div>
      )}

      <div className="flex mt-2 pt-2 border-t border-slate-100">
        <Link
          to="/ops?tab=coaching"
          onClick={() => logCardClick("widget_coaching_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Coaching board ›
        </Link>
      </div>
    </div>
  );
}
